export const printSurveyResponses = async (
  responses,
  surveyTitle = "No survey title"
) => {
  if (!responses || responses.length === 0) {
    console.error("No responses to print");
    return;
  }

  const printWindow = window.open("", "_blank");

  const renderTextQuestion = (question) => {
    if (!question.answers || question.answers.length === 0) {
      return `<p style="color: #868e96; font-style: italic;">No answers yet (0 responses)</p>`;
    }

    return question.answers
      .map(
        (ansObj) => `
      <div style="
        border: 1px solid #dee2e6;
        border-radius: 4px;
        padding: 12px;
        margin-bottom: 8px;
        background: white;
        break-inside: avoid;
      ">
        <p style="margin: 0; color: #212529;">
          ${ansObj.answer} <span style="color: #868e96;">(${ansObj.count})</span>
        </p>
      </div>
    `
      )
      .join("");
  };

  const renderChoiceQuestion = (question) => {
    const total = question.totalResponses || 0;

    if (!question.answerOptions || question.answerOptions.length === 0) {
      return `<p style="color: #868e96; font-style: italic;">No answer options</p>`;
    }

    return question.answerOptions
      .map((opt) => {
        const count = opt.count || 0;
        const percent = total > 0 ? (count / total) * 100 : 0;

        return `
      <div style="
        border: 1px solid #dee2e6;
        border-radius: 4px;
        padding: 12px;
        margin-bottom: 8px;
        background: white;
        break-inside: avoid;
      ">
        <p style="margin: 0 0 8px 0; color: #212529;">
          ${opt.labelI18n?.en || opt.label || "Unlabeled option"} 
          <span style="color: #868e96;">
            (${count} / ${total} ~ ${Math.round(percent)}%)
          </span>
        </p>
        <div style="
          width: 100%;
          height: 8px;
          background-color: #e9ecef;
          border-radius: 4px;
          overflow: hidden;
        ">
          <div style="
            height: 100%;
            background-color: #228be6;
            width: ${percent}%;
            transition: width 0.3s;
          "></div>
        </div>
      </div>
    `;
      })
      .join("");
  };

  const renderQuestion = (question, index, total) => {
    const questionTypeLabel = question.questionType
      .replace("_", " ")
      .toUpperCase();

    let answersHTML = "";
    if (question.questionType === "text") {
      answersHTML = renderTextQuestion(question);
    } else if (
      question.questionType === "single_choice" ||
      question.questionType === "multiple_choice"
    ) {
      answersHTML = renderChoiceQuestion(question);
    }

    return `
       <div style="
      border: 1px solid #dee2e6;
      border-radius: 8px;
      padding: 24px;
      margin-bottom: 24px;
      background: white;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
      break-inside: avoid;
      page-break-inside: avoid;
    ">
      <div style="
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
      ">
        <h3 style="
          margin: 0;
          color: #212529;
          font-size: 20px;
          font-weight: 600;
        ">
          Question ${index + 1} / ${total}
        </h3>
        
        <span style="
          display: inline-block;
          padding: 6px 12px;
          background-color: #e7f5ff;
          color: #228be6;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 500;
        ">
          Total responses: ${question.totalResponses || 0}
        </span>
      </div>
      
      <p style="
        margin: 0 0 16px 0;
        font-weight: 500;
        color: #212529;
        font-size: 16px;
      ">
        ${question.questionText} 
        <span style="color: #868e96; font-weight: normal; font-size: 14px;">
          (${questionTypeLabel})
        </span>
      </p>

      <div style="margin-top: 16px;">
        ${answersHTML}
      </div>
    </div>
    `;
  };

  const questionsHTML = responses
    .map((question, index) => renderQuestion(question, index, responses.length))
    .join("");

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${surveyTitle} - Print</title>
      <style>
        @media print {
          @page {
            size: A4 portrait;
            margin: 20mm;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Prevent page breaks inside question blocks */
          .question-block {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          /* Prevent orphaned titles */
          h3 {
            page-break-after: avoid;
            break-after: avoid;
          }
        }
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
          background-color: #f8f9fa;
          padding: 20px;
          line-height: 1.5;
        }
        
        .container {
          max-width: 800px;
          margin: 0 auto;
        }
        
        .header {
          text-align: center;
          margin-bottom: 32px;
          padding-bottom: 16px;
          border-bottom: 2px solid #dee2e6;
          page-break-after: avoid;
          break-after: avoid;
        }
        
        .header h1 {
          color: #212529;
          font-size: 28px;
          margin-bottom: 8px;
        }

        .header h3 {
          color: #212529;
          margin-bottom: 8px;
        }
      
        .header p {
          color: #868e96;
          font-size: 14px;
        }
        
        .subnotes {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>Survey Responses</h1>
          <h3>${surveyTitle}</h3>
          <div class="subnotes">
            <p style="margin-top: 4px;">Total Questions: ${responses.length}</p>
            <p>Print date: ${new Date().toLocaleDateString()}</p>
          </div>
        </div>
        
        ${questionsHTML}
      </div>
      
      <script>
        // Wait for content to load before printing
        window.onload = () => {
          setTimeout(() => {
            window.print();
          }, 500);
        };
        
        // Close window after printing
        window.onafterprint = () => {
          window.close();
        };
      </script>
    </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
};
